
import { Invoice, Filters } from "@/types/payments";
import { compareMissingLast } from "@/utils/compareMissingLast";

// Filter invoices based on tab selection
export const getFilteredInvoicesByTab = (invoices: Invoice[], tab: string) => {
  const today = new Date();
  
  switch (tab) {
    case "unpaid":
      return invoices.filter(invoice =>
        invoice.status === "unpaid"
      );
    case "pending":
      return invoices.filter(invoice => invoice.status === "pending");
    case "paid":
      return invoices.filter(invoice => invoice.status === "paid" || invoice.status === "manually_handled");
    case "overdue":
      return invoices.filter(invoice => {
        const dueDate = new Date(invoice.dueDate);
        return dueDate < today && invoice.status !== "paid" && invoice.status !== "manually_handled";
      });
    case "all":
    default:
      return invoices;
  }
};

// Filter out unapproved invoices
export const filterUnapprovedInvoices = (invoices: Invoice[]): Invoice[] => {
  return invoices.filter(invoice => invoice.approvedBy !== undefined);
};

// An amount filter cannot place a document with no amount yet, so it excludes it.
const matchesAmountRange = (amount: number | null, filters: Filters): boolean => {
  if (!filters.minAmount && !filters.maxAmount) return true;
  if (amount === null) return false;
  if (filters.minAmount && amount < parseFloat(filters.minAmount)) return false;
  if (filters.maxAmount && amount > parseFloat(filters.maxAmount)) return false;
  return true;
};

// Apply additional filters to invoices
export const applyFilters = (invoices: Invoice[], filters: Filters) => {
  return invoices.filter(invoice => {
    // Filter by status
    if (filters.status !== 'all' && invoice.status !== filters.status) {
      return false;
    }
    
    // Filter by vendor
    if (filters.vendor && !invoice.vendor.toLowerCase().includes(filters.vendor.toLowerCase())) {
      return false;
    }
    
    if (!matchesAmountRange(invoice.amount, filters)) {
      return false;
    }

    // Filter by currency (supports multiple currencies or 'all')
    if (filters.currency !== 'all' &&
        Array.isArray(filters.currency) &&
        !filters.currency.includes(invoice.currency)) {
      return false;
    }

    // Filter by due date range
    if (filters.dueDateFrom && new Date(invoice.dueDate) < new Date(filters.dueDateFrom)) {
      return false;
    }
    
    if (filters.dueDateTo && new Date(invoice.dueDate) > new Date(filters.dueDateTo)) {
      return false;
    }
    
    // Filter by payment date range (if payment date exists)
    if (filters.paymentDateFrom && invoice.paymentDate && 
        new Date(invoice.paymentDate) < new Date(filters.paymentDateFrom)) {
      return false;
    }
    
    if (filters.paymentDateTo && invoice.paymentDate && 
        new Date(invoice.paymentDate) > new Date(filters.paymentDateTo)) {
      return false;
    }
    
    return true;
  });
};

// Sort invoices based on sort configuration
export const sortInvoices = (
  invoices: Invoice[], 
  sortConfig: { key: keyof Invoice | null; direction: 'asc' | 'desc' | 'none'; }
) => {
  if (!sortConfig.key || sortConfig.direction === 'none') {
    return invoices;
  }
  
  return [...invoices].sort((a, b) => {
    const aValue = a[sortConfig.key!];
    const bValue = b[sortConfig.key!];
    
    // Handle date comparison
    if (sortConfig.key === 'dueDate' || sortConfig.key === 'paymentDate') {
      const aDate = aValue ? new Date(aValue as string).getTime() : 0;
      const bDate = bValue ? new Date(bValue as string).getTime() : 0;
      return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
    }

    // A missing value (e.g. an unknown amount, FIN-413) sorts last in both directions.
    const missing = compareMissingLast(aValue, bValue);
    if (missing !== null) return missing;

    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    // Handle number comparison
    return sortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });
};

// Determine invoice status based on due date and current status
export const determineInvoiceStatus = (invoice: Invoice): "unpaid" | "overdue" => {
  const today = new Date();
  const dueDate = new Date(invoice.dueDate);
  
  return dueDate < today ? "overdue" : "unpaid";
};
